import classNames from "classnames";
import Breadcrumb from "components/DesignSystem/Breadcrumb/Breadcrumb";
import { Button } from "components/DesignSystem/Button/Button";
import Modal from "components/DesignSystem/Modal/Modal";
import React, { useState } from "react";
import { ModalProps } from "types/utils/modal";
import { FileIcon } from "components/design/FileIcon";
import Prevfolder from "static/images/Prevfolder.svg";
import {
  useGetEntityFolderQuery,
  useGetFolderContentQuery,
  useGetTagDocumentsQuery,
} from "store/apis/documents";
import { useCurrentGroupContext } from "hooks/useCurrentGroupContext";
import { useToast } from "hooks/useToast";
import { BackendError } from "types/utils/error";
import Async from "components/DesignSystem/AsyncComponents/Async";
import { FOLDER_TYPE } from "constants/documents";
import { SelecteFile } from "types/Models/documents";

export type UploadFromInkleDocsFileSelectorModalProps = {
  groupId?: string;
  fileTag?: string;
  entityId?: string;
  folderPath?: string[];
  allowSingleUpload?: boolean;
  onUpload: (files: SelecteFile[]) => void;
} & ModalProps;

export const UploadFromInkleDocsFileSelectorModal = ({
  isOpen,
  close,
  groupId: groupIdFromProps,
  fileTag,
  entityId,
  folderPath,
  allowSingleUpload = true,
  onUpload: onUploadFromProps,
}: UploadFromInkleDocsFileSelectorModalProps) => {
  const { uuid: currentGroupId } = useCurrentGroupContext();
  const { alertToast } = useToast();

  const [selectedFiles, setSelectedFiles] = useState<SelecteFile[]>([]);

  const groupId = groupIdFromProps || currentGroupId || "";

  const { data: folderWithTagFile, ...fileWithTagStates } =
    useGetTagDocumentsQuery(
      {
        groupId: groupId,
        tag_title: fileTag,
      },
      { skip: !Boolean(groupId) || !Boolean(isOpen) }
    );

  const { parents_list: parentList = [] } = folderWithTagFile || {};
  const serverFolderId = parentList.at(-1)?.uuid;

  const {
    data: entityFolder,
    isSuccess: isEntityFolderSuccess,
    isError: isEntityFolderError,
    isLoading: isEntityFolderLoading,
  } = useGetEntityFolderQuery(
    { entityId: entityId || "", folderPath: folderPath || [] },
    { skip: !entityId || !folderPath?.length || !isOpen }
  );
  const entityFolderId = entityFolder?.uuid;

  // Wait for the entity folder lookup to settle before falling back to the
  // tag-based default, which always resolves to something (the group root
  // when there's no matching tag) and would otherwise win the race.
  const isEntityFolderPending =
    Boolean(entityId && folderPath?.length) &&
    !isEntityFolderSuccess &&
    !isEntityFolderError;

  const [localFolderId, setLocalFolderId] = useState("");
  const folderId =
    localFolderId ||
    entityFolderId ||
    (isEntityFolderPending ? "" : serverFolderId) ||
    "";

  const {
    data: folderData,
    refetch,
    ...folderStates
  } = useGetFolderContentQuery(
    {
      groupId: groupId,
      folderId: folderId,
    },
    { skip: Boolean(!folderId) || !Boolean(groupId) || !Boolean(isOpen) }
  );

  const { files = [], subfolders = [], parents_list = [] } = folderData || {};
  const folderContent = [...subfolders, ...files];
  const isEmpty = folderContent.length === 0;

  const isLoading =
    folderStates.isLoading ||
    fileWithTagStates.isLoading ||
    isEntityFolderLoading;
  const isSuccess = folderStates.isSuccess && fileWithTagStates.isSuccess;
  const isFetching = folderStates.isFetching || fileWithTagStates.isFetching;

  const { [parents_list.length - 2]: parentFolder } = parents_list;

  const folderClicked = ({ folderId }: { folderId: string }) => {
    try {
      setLocalFolderId(folderId);
      refetch().unwrap();
    } catch (error) {
      alertToast({
        message: (error as BackendError).data?.error?.message,
      });
    }
  };

  const onBackButtonClick = () => {
    if (parentFolder?.uuid) {
      folderClicked({ folderId: parentFolder?.uuid });
    } else {
      close();
    }
  };

  const onFileClick = ({
    uuid,
    name,
    file_type,
    is_previewable,
    type,
  }: SelecteFile & { type: string }) => {
    if (type === FOLDER_TYPE) return;
    if (allowSingleUpload) {
      setSelectedFiles([{ uuid, name, file_type, is_previewable }]);
      return;
    }
    setSelectedFiles((prev) => {
      const isSelected = prev.some((f) => f.uuid === uuid);
      if (isSelected) {
        return prev.filter((f) => f.uuid !== uuid);
      }
      return [...prev, { uuid, name, file_type, is_previewable }];
    });
  };

  const onUpload = () => {
    close();
    onUploadFromProps?.(selectedFiles);
    setSelectedFiles([]);
  };

  return (
    <Modal.Root open={isOpen} onOpenChange={close}>
      <Modal.Content size="regular">
        <Modal.Header>
          <div>
            <Modal.Title>
              <div className="t-flex t-items-center t-gap-1">
                <Button
                  customType="ghost_icon"
                  size="small"
                  onClick={onBackButtonClick}
                  disabled={isFetching}
                  isLoading={isFetching}
                >
                  <img src={Prevfolder} alt="Back" />
                </Button>
                <span className="t-max-w-80 t-truncate t-text-body-sm">
                  {parents_list.at(-1)?.name}
                </span>
              </div>
            </Modal.Title>
            {isSuccess && (
              <Modal.Subtitle>
                <Breadcrumb.Root itemsToDisplay={3}>
                  {parents_list.map(({ uuid, name }) => (
                    <Breadcrumb.Item
                      key={uuid}
                      as="button"
                      onClick={() => folderClicked({ folderId: uuid })}
                    >
                      <div className="t-line-clamp-1">{name}</div>
                    </Breadcrumb.Item>
                  ))}
                </Breadcrumb.Root>
              </Modal.Subtitle>
            )}
          </div>

          <Modal.Close />
        </Modal.Header>
        <Modal.Body>
          <Async.Root
            isEmpty={isEmpty}
            isLoading={isLoading}
            isSuccess={isSuccess}
          >
            <Async.Empty>
              <div className="t-flex t-justify-center t-items-center t-w-full t-text-neutral-40 t-h-80">
                This folder is empty
              </div>
            </Async.Empty>
            <Async.Success>
              <div className="t-flex t-h-80 t-flex-col t-items-start t-gap-1 t-select-none">
                {folderContent.map(
                  ({ uuid, name, file_type, type, is_previewable }) => {
                    return (
                      <button
                        key={uuid}
                        onDoubleClick={() =>
                          type === FOLDER_TYPE &&
                          folderClicked({ folderId: uuid })
                        }
                        onClick={() =>
                          onFileClick({
                            uuid,
                            name,
                            file_type,
                            is_previewable,
                            type,
                          })
                        }
                        className={classNames(
                          "all:unset t-flex t-w-full t-cursor-pointer t-items-center t-gap-2 t-border-solid t-border-surface-transparent t-px-2 t-py-1 hover:t-rounded hover:t-bg-purple-10 focus-visible:t-rounded focus-visible:t-bg-purple-10 focus-visible:t-outline focus-visible:t-outline-purple-30",
                          {
                            "t-rounded t-border-solid t-border-purple-30 t-bg-purple-10":
                              selectedFiles.some((f) => f.uuid === uuid),
                          }
                        )}
                      >
                        <FileIcon fileType={file_type} size="32" />
                        <div className="t-max-w-[25rem] t-truncate t-text-body-sm">
                          {name}
                        </div>
                      </button>
                    );
                  }
                )}
              </div>
            </Async.Success>
          </Async.Root>
        </Modal.Body>
        <Modal.Footer className="t-flex t-justify-between t-items-center">
          {selectedFiles.length > 0 && (
            <div className="t-text-body-sm t-text-text-30">
              {selectedFiles.length} file{selectedFiles.length > 1 ? "s" : ""}{" "}
              selected
            </div>
          )}
          <div className="t-ml-auto">
            <Button
              customType="primary"
              size="small"
              onClick={onUpload}
              disabled={selectedFiles.length === 0}
            >
              Insert
            </Button>
          </div>
        </Modal.Footer>
      </Modal.Content>
    </Modal.Root>
  );
};
